import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { invalidateComplianceQueries } from "@/lib/compliance/invalidate-compliance-queries";
import { offlineDb, type SyncQueueItem } from "@/lib/offline/db";
import { MAX_RETRIES, syncService } from "@/lib/offline/syncService";

/**
 * Queue item types that change compliance data once they reach the server.
 * Typed against the queue's own union so renaming a type there fails the build
 * rather than silently leaving this hook matching nothing.
 */
const COMPLIANCE_QUEUE_TYPES: SyncQueueItem["type"][] = [
  "complianceFinalize",
  "compliancePhoto",
];

/**
 * Refresh every compliance surface when queued compliance work finishes
 * syncing, wherever the user happens to be at the time.
 *
 * A completion submitted offline reaches the server minutes later, long after
 * the completion screen unmounted — nothing else in the app invalidates the
 * cache at that point, so the Compliance Manager and dashboard would keep
 * serving their pre-completion tables.
 *
 * It matters online too, which is less obvious. A finalize whose instance still
 * has photos uploading is thrown back onto the queue rather than sent, but the
 * submit-time `immediateSync()` resolves anyway — so the sweep that runs on
 * submit reads a server that doesn't have the completion yet. Here the count
 * stays above zero across that premature sweep and only drains once the photos
 * and the finalize have both landed, which is what corrects it.
 *
 * Mount once, high in the authenticated tree.
 */
export function useComplianceSyncRefresh(): void {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  const previousPendingRef = useRef(0);
  // Checks run one after another rather than concurrently — see below.
  const checkChainRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    queryClientRef.current = queryClient;
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = syncService.subscribe(() => {
      // Queued one behind the last, so the counts are read in the order the
      // notifications arrived and every one of them is recorded.
      //
      // An earlier version ran them concurrently and kept only the newest,
      // discarding any result a later check had superseded. That drops the very
      // observation the transition depends on: a sync that succeeds first time
      // notifies at its start (queue holds the item) and again at its finish
      // (queue empty) a few milliseconds apart, so the opening count was always
      // superseded before it resolved. The hook then compared empty against
      // empty, saw no transition, and never refreshed.
      checkChainRef.current = checkChainRef.current
        .then(async () => {
          // Counted here rather than read off the status object, which reports
          // the whole queue: unrelated work would fire this on every drain, and
          // a single permanently-failed item of any type would hold the total
          // above zero so the transition never came again. Items that have
          // exhausted their retries are excluded for the same reason.
          const pending = await offlineDb.syncQueue
            .filter(
              (item) =>
                COMPLIANCE_QUEUE_TYPES.includes(item.type) &&
                item.attempts < MAX_RETRIES,
            )
            .count();

          const previous = previousPendingRef.current;
          previousPendingRef.current = pending;

          if (previous > 0 && pending === 0) {
            await invalidateComplianceQueries(queryClientRef.current);
          }
        })
        .catch((err) => {
          console.error("compliance sync refresh check failed", err);
        });
    });

    return () => {
      unsubscribe();
    };
  }, []);
}
